import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * Estimates how much a plug-in solar kit could take off an electricity bill.
 *
 * Savings only come from electricity used in the home while it is being
 * generated — a plug-in system has no export payment — so the self-consumption
 * share is part of the model, not a detail.
 *
 * All coefficients arrive as data attributes so merchants stay in control of
 * every number through the block schema.
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement} kit
 * @property {HTMLSelectElement} orientation
 * @property {HTMLInputElement} rate
 * @property {HTMLElement} annualSaving
 * @property {HTMLElement} monthlySaving
 * @property {HTMLElement} generation
 * @property {HTMLInputElement} [bill]
 * @property {HTMLElement} [offset]
 * @property {HTMLElement} [offsetRow]
 * @property {HTMLElement} [capNote]
 *
 * @extends {Component<Refs>}
 */
export class SavingsCalculatorComponent extends Component {
  requiredRefs = ['kit', 'orientation', 'rate', 'annualSaving', 'monthlySaving', 'generation'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#stopObserving();
        this.update();
      },
      { rootMargin: '200px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopObserving();
  }

  #stopObserving() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Formats an amount in pounds using the shop's own money format, so the
   * client-side output matches Liquid's `money` filter rather than the
   * visitor's locale conventions.
   *
   * @param {number} amount - Amount in the shop's major currency unit.
   * @returns {string}
   */
  #formatMoney(amount) {
    return formatMoney(
      Math.round(amount * 100),
      this.dataset.moneyFormat || '{{amount}}',
      this.dataset.currency || 'GBP'
    );
  }

  /**
   * Reads a positive number from an input, falling back to a default when the
   * field is empty or holds something that isn't a usable number.
   *
   * @param {HTMLInputElement | undefined} input
   * @param {number} fallback
   * @returns {number}
   */
  #readNumber(input, fallback) {
    if (!input) return fallback;

    const value = Number.parseFloat(input.value);

    if (!Number.isFinite(value) || value < 0) return fallback;

    return value;
  }

  /**
   * Reads a coefficient from the selected option of a select element.
   *
   * @param {HTMLSelectElement} select
   * @param {string} key
   * @returns {number}
   */
  #readOption(select, key) {
    const option = select.selectedOptions[0];
    const value = Number.parseFloat(option?.dataset[key] ?? '');

    return Number.isFinite(value) ? value : 0;
  }

  /** Recalculates the estimate and writes it into the output elements. */
  update() {
    const { kit, orientation, rate, bill, annualSaving, monthlySaving, generation, offset, offsetRow, capNote } =
      this.refs;

    const kitYield = this.#readOption(kit, 'kwh');
    const factor = this.#readOption(orientation, 'factor') / 100;
    const share = Number.parseFloat(this.dataset.selfConsumption ?? '70') / 100;
    const pencePerKwh = this.#readNumber(rate, Number.parseFloat(this.dataset.defaultRate ?? '25'));

    const generated = kitYield * factor;
    const selfUsed = generated * share;
    const uncapped = selfUsed * (pencePerKwh / 100);

    const monthlyBill = bill ? this.#readNumber(bill, 0) : 0;
    const annualBill = monthlyBill * 12;
    const capped = annualBill > 0 ? Math.min(uncapped, annualBill) : uncapped;

    generation.textContent = String(Math.round(generated));
    annualSaving.textContent = this.#formatMoney(capped);
    monthlySaving.textContent = this.#formatMoney(capped / 12);

    if (offset && offsetRow) {
      if (annualBill > 0) {
        offset.textContent = `${Math.round((capped / annualBill) * 100)}%`;
        offsetRow.hidden = false;
      } else {
        offsetRow.hidden = true;
      }
    }

    if (capNote) {
      capNote.hidden = !(annualBill > 0 && uncapped > annualBill);
    }
  }
}

if (!customElements.get('savings-calculator')) {
  customElements.define('savings-calculator', SavingsCalculatorComponent);
}
